import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import useScrollDirection from '@/hooks/useScrollDirection';
import { ThemeContext } from '@/state/Theme';
import AlertBanner from '@/components/AlertBanner';
import CloseIcon from '@/icons/CloseIcon';
import HamburgerIcon from '@/icons/HamburgerIcon';
import Nav from '@/components/Nav';
import MegaNav from '@/components/MegaNav';
import MobileMenu from '@/components/MobileMenu';
import SearchIcon from '@/icons/SearchIcon';
import CategoryNav from '@/components/CategoryNav';
import ButtonCircle from '@/components/ButtonCircle';
import SearchForm from '@/components/Search/SearchForm';
import LoginMenu from '@/components/LoginMenu/LoginMenu';

import { ChevronDown } from '@/icons';
import { type IHeaderData, type INavItem } from './Header.types';
import {
  commonTransition,
  searchChildVariants,
  searchVariants,
} from './Header.animations';

const SINGLE_MENU = 'single';

const Header: React.FC<IHeaderData> = (props) => {
  const {
    findStationText,
    findStationUrl,
    nav,
    subNav,
    loginMenu,
    includeSearch,
    helpPageText,
    helpPageUrl,
    searchResultsUrl,
    contactNumber,
    contactText,
    contactUrl,
    loginCTA,
    bannerText,
    bannerTextMobile,
    bannerLink,
    bannerLinkText,
    menuDesign = 'double',
  } = props;

  const arrowHeight = 13;
  const arrowWidth = 17;
  const [currentPageID, setCurrentPageID] = useState<string>(undefined);
  const [categoryLinks, setCategoryLinks] = useState<INavItem[]>([]);
  const [topLevelLinks, setTopLevelLinks] = useState<INavItem[]>([]);
  const [subNavLinks, setSubNavLinks] = useState<INavItem[]>([]);
  const [loginMenuLinks, setloginMenuLinks] = useState<INavItem[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [megaMenuPage, setMegaMenuPage] = useState<INavItem>(undefined);
  const [loginMenuOpen, setLoginMenuOpen] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const scrollDirection = useScrollDirection(20);
  const showCategoryNav = categoryLinks.length > 0;
  const showSubNav = subNavLinks.length > 0;

  const menuToggle = useRef(null);

  /**
   * Set the active page id and open the mega menu
   * @param id the id of the page to set as active
   */
  const handleNavLinkClick = (id: string) => {
    setCurrentPageID(currentPageID === id ? undefined : id);
    setMegaMenuOpen(currentPageID !== id);

    if (includeSearch && showSearch) {
      setShowSearch(false);
    }
  };

  // add a esc key handler, if mega menu is open or search is open, close it
  // and move focus back
  useEffect(() => {
    const handleKeydown = (e) => {
      const { activeElement } = document;

      // esc key press
      if (e.key === 'Escape' && e.keyCode == 27) {
        if (megaMenuOpen) {
          setMegaMenuOpen(false);
          setCurrentPageID(undefined);
        }
        if (mobileMenuOpen) {
          setMobileMenuOpen(false);
          setTimeout(() => {
            menuToggle.current?.focus();
          }, 400);
        }
        if (showSearch) {
          setShowSearch(false);
        }
      }

      // tab key press
      if (e.key === 'Tab' && e.keyCode == 9) {
        if (activeElement.id === 'mobile-login') {
          setTimeout(() => {
            menuToggle.current?.focus();
          }, 10);
        }
      }

      // shift + tab key press
      if (e.shiftKey && e.keyCode == 9) {
        if (activeElement.id === 'header-logo') {
          setTimeout(() => {
            menuToggle.current?.focus();
          }, 100);
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [megaMenuOpen, showSearch]);

  // add a useEffect so if search is open a class is added to the body
  // to prevent scrolling
  useEffect(() => {
    if (showSearch) {
      document.body.classList.add('search-open');
    } else {
      document.body.classList.remove('search-open');
    }
  }, [showSearch]);

  /**
   * Toggle the login dropdown menu
   */
  const toggleLoginMenu = () => {
    setLoginMenuOpen(!loginMenuOpen);

    if (showSearch) {
      setShowSearch(false);
    }
  };

  /**
   * Toggle the mobile menu
   */
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);

    if (showSearch) {
      setShowSearch(false);
    }
  };

  /**
   * Toggle the search bar
   */
  const toggleSearchBar = () => {
    setShowSearch(!showSearch);

    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  /**
   * Keep the header height value up to date
   * because the content of it is dynamic
   */
  useEffect(() => {
    const header = document.querySelector('.header') as HTMLElement;

    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        const value = header?.clientHeight ?? 0;
        setHeaderHeight(value);
      });
      resizeObserver.observe(header);
      return () => resizeObserver.disconnect(); // clean up
    }
  }, []);

  /**
   * Set the header height as a css variable
   */
  useEffect(() => {
    const rootStyles = document.documentElement.style;
    if (headerHeight > 0) {
      rootStyles.setProperty('--header-height', `${headerHeight}px`);
    }
  }, [headerHeight]);

  /**
   * Set the active page for the mega menu
   */
  useEffect(() => {
    if (topLevelLinks && currentPageID) {
      const activePage = topLevelLinks.find(
        (item) => item.id === currentPageID,
      );
      setMegaMenuPage(activePage || undefined);
    }
  }, [currentPageID]);

  /**
   * Extract the relevant page data based on selected pages
   */
  useEffect(() => {
    /* Primary Nav */
    if (nav.length > 0) {
      const topLinks: INavItem[] = [];

      if (menuDesign === SINGLE_MENU) {
        setTopLevelLinks(nav);
      } else {
        const links = nav.filter((category) => {
          if (!category.hidden) {
            if (category.displayOnlyOnMobile && !isMobile) {
              return false;
            }
            return true;
          }

          return false;
        });

        const hasOneHighlight =
          links.filter((link) => link.linkingMode !== 'link').length > 0;

        nav.map((link, i) => {
          if (
            link.children &&
            (link.linkingMode !== 'link' || (!hasOneHighlight && i === 0))
          ) {
            if (link.displayOnlyOnMobile && !isMobile) {
              return true;
            }

            topLinks.push(...link.children);
          }

          return true;
        });

        setCategoryLinks(links);

        setTopLevelLinks(topLinks);
      }
    }

    /* Secondary Nav */
    if (subNav.length > 0) {
      const subNavItems = subNav.filter((item) => {
        if (item.displayOnlyOnMobile && !isMobile) {
          return false;
        }
        return true;
      });

      setSubNavLinks(subNavItems);
    }

    /* Login Menu */
    if (loginMenu.length > 0) {
      const loginMenuItems = loginMenu.filter((item) => {
        if (item.displayOnlyOnMobile && !isMobile) {
          return false;
        }
        return true;
      });

      setloginMenuLinks(loginMenuItems);
    }
  }, [nav, subNav, loginMenu, isMobile]);

  /**
   * Close the mega menu and search if the header is hidden on scroll
   */
  useEffect(() => {
    if (scrollDirection === 'down' && megaMenuOpen) {
      setMegaMenuOpen(false);
      setCurrentPageID(undefined);
    }
    if (scrollDirection === 'down' && loginMenuOpen) {
      setLoginMenuOpen(false);
    }
    if (scrollDirection === 'down' && showSearch) {
      setShowSearch(false);
    }
  }, [scrollDirection]);

  /**
   * Prevent scrolling when the mobile menu is open
   */
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    const headerWrapper = document.querySelector<HTMLElement>(
      '[data-component="header"]',
    );
    rootStyle.overflowY = mobileMenuOpen ? 'hidden' : 'visible';
    headerWrapper.style.height = mobileMenuOpen ? '100%' : '0';

    // If we leave the mobile breakpoint
    // close the mobile menu and reset the overflow
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;

      setIsMobile(width <= 992);

      if (width > 992) {
        setMobileMenuOpen(false);
      }
    });
    observer.observe(document.documentElement);

    // clean up
    return () => {
      rootStyle.overflowY = 'visible';
      observer.disconnect();
    };
  }, [mobileMenuOpen]);

  let hasSecondNav =
    menuDesign !== SINGLE_MENU &&
    topLevelLinks.filter((link) => link.children && link.children.length > 0)
      .length > 0;

  if (showCategoryNav && !hasSecondNav && menuDesign !== SINGLE_MENU) {
    hasSecondNav = subNavLinks.length > 0;
  }

  const topLevelIsMega = menuDesign === SINGLE_MENU;

  return (
    <ThemeContext.Consumer>
      {({ brandLogo, brandLogoMobile }) => (
        <>
          <motion.header
            className="header"
            key="header"
            initial={false}
            animate={{ y: scrollDirection === 'down' ? '-100%' : 0 }}
            transition={{ bounce: 0 }}
            style={{
              zIndex: 3,
              pointerEvents: scrollDirection === 'down' ? 'none' : 'initial',
            }} // dropdown layering
          >
            <AlertBanner
              text={bannerText}
              textMobile={bannerTextMobile}
              link={bannerLink}
              linkText={bannerLinkText}
              contactText={contactText}
              contactUrl={contactUrl}
            />
            <div className="header__row header__row--first">
              <div className="header__container container">
                <div className="header__column">
                  {/* Logo */}
                  <a
                    href="/"
                    id="header-logo"
                    className="header__logo"
                    title="Go back to the home page"
                  >
                    {typeof brandLogo === 'string' ? (
                      <img src={brandLogo} alt="Logo" />
                    ) : (
                      brandLogo
                    )}
                  </a>

                  {brandLogoMobile && (
                    <a
                      href="/"
                      className="header__logo header__logo--mobile"
                      title="Go back to the home page"
                    >
                      {brandLogoMobile}
                    </a>
                  )}

                  {/* Category Nav (Business / Personal) */}
                  {topLevelIsMega && (
                    <Nav
                      currentId={currentPageID}
                      pages={topLevelLinks}
                      onClick={(id) => handleNavLinkClick(id)}
                    />
                  )}

                  {!topLevelIsMega && showCategoryNav && (
                    <CategoryNav pages={categoryLinks} />
                  )}
                </div>

                <div className="header__column">
                  {showSubNav && (
                    <ul className="header__subnav">
                      {subNavLinks.map((link, i) => {
                        const {
                          title,
                          url,
                          linkingMode,
                          displayOnlyOnMobile,
                          isNewWindow,
                        } = link;

                        if (displayOnlyOnMobile && !isMobile) {
                          return null;
                        }

                        return (
                          <li key={i} className="header__subnav-item">
                            <a
                              href={url}
                              target={isNewWindow && '_blank'}
                              className={`header__subnav-link header__subnav-link--${linkingMode}`}
                            >
                              {title}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {includeSearch && searchResultsUrl && (
                    <button
                      className={cn('header__search', {
                        'header__search--active': showSearch,
                      })}
                      onClick={toggleSearchBar}
                      aria-labelledby="header-search"
                    >
                      <SearchIcon
                        className="header__search-icon"
                        width={16}
                        height={16}
                      />
                    </button>
                  )}

                  {loginCTA && (
                    <div className="header__login">
                      <button
                        className={cn(
                          'header__login-button button button--primary',
                          {
                            'header__login-button--active': loginMenuOpen,
                          },
                        )}
                        onClick={toggleLoginMenu}
                      >
                        Login
                        <ButtonCircle
                          arrowHeight={arrowHeight}
                          arrowWidth={arrowWidth}
                          extraClasses="login-button"
                          customIcon={<ChevronDown />}
                        />
                      </button>

                      {loginMenuOpen && (
                        <LoginMenu
                          onClickOutside={() => {
                            setLoginMenuOpen(false);
                          }}
                          loginMenu={loginMenuLinks}
                        />
                      )}
                    </div>
                  )}

                  <button
                    className="header__hamburger"
                    onClick={toggleMobileMenu}
                    aria-label="Header hamburger icon"
                    ref={menuToggle}
                  >
                    {mobileMenuOpen ? (
                      <CloseIcon size={18} />
                    ) : (
                      <HamburgerIcon width={37} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* MAIN NAV */}
            {hasSecondNav && (
              <div className="header__row header__row--second">
                <div className="header__container container">
                  <Nav
                    currentId={currentPageID}
                    pages={topLevelLinks}
                    onClick={(id) => handleNavLinkClick(id)}
                  />
                </div>
              </div>
            )}
          </motion.header>

          {/* MEGA MENU */}
          <AnimatePresence>
            {megaMenuPage && megaMenuOpen && (
              <div className="container pos-r">
                <MegaNav
                  onClickOutside={() => {
                    setCurrentPageID(undefined);
                    setMegaMenuOpen(false);
                  }}
                  page={megaMenuPage}
                  helpPageText={helpPageText}
                  helpPageUrl={helpPageUrl}
                  transition={commonTransition}
                />
              </div>
            )}
          </AnimatePresence>

          {/* SEARCH BAR */}
          {searchResultsUrl && (
            <AnimatePresence>
              {includeSearch && showSearch && (
                <motion.div
                  className="header__search-bar pos-r"
                  key="search-bar"
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0.9, y: '-100%' }}
                  variants={searchVariants}
                  transition={commonTransition}
                  style={{ zIndex: 1 }} // dropdown layering
                >
                  <motion.div
                    className="container"
                    key="search-form"
                    variants={searchChildVariants}
                    transition={{ ...commonTransition, damping: 15 }}
                  >
                    <SearchForm
                      showFacets={false}
                      hasHits={false}
                      helpCentre={false}
                      hasHelpCentre={helpPageUrl && helpPageUrl.length > 0}
                      focusOnMount
                      resultsUrl={searchResultsUrl}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* MOBILE MENU */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <MobileMenu
                nav={nav}
                subNav={subNavLinks}
                loginCTA={loginCTA}
                loginMenu={loginMenuLinks}
                contactNumber={contactNumber}
                findStationText={findStationText}
                findStationUrl={findStationUrl}
                headerHeight={headerHeight}
                transition={commonTransition}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </ThemeContext.Consumer>
  );
};

export default Header;
